<template>
    <nw-dialog
        v-model="dialogOpen"
        :attached-to="dialogAnchorSelector"
        class="nw-chat-dialog"
        :close-on-outside-click="false"
        :label="t('chat.dialog.label')"
        max-width="900px"
        no-actions
        no-padding
        width="900px"
        @closed="emit('close')"
    >
        <template #headerActions>
            <nw-btn
                icon="refresh"
                small
                :title="t('chat.dialog.newConversation')"
                @click="resetConversation"
            />
        </template>

        <div class="chat-dialog__body">
            <ChatHeader />
            <ChatMessageList :is-thinking="isThinking" :messages="messages" />
            <ChatInput
                :disabled="isStreaming"
                :is-streaming="isStreaming"
                @abort="abortStream"
                @send="sendMessage"
            />
        </div>
    </nw-dialog>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useChat } from '@/composables/useChat';
import ChatHeader from './ChatHeader.vue';
import ChatInput from './ChatInput.vue';
import ChatMessageList from './ChatMessageList.vue';

defineOptions({
    name: 'ChatDialog',
});

const props = defineProps<{
    open: boolean;
    apiUrl: string;
}>();

const emit = defineEmits<{ close: [] }>();

const { t } = useI18n();

const { messages, isStreaming, isThinking, sendMessage, abortStream, resetConversation } = useChat(
    props.apiUrl,
);

// nw-dialog Teleports into whatever `attached-to` resolves to, falling back to
// `document.body` when nothing matches. Teleporting straight into
// `document.body` triggers a Vue 3 runtime-core bug (`getNextHostNode` /
// `hostNextSibling` throwing on a null anchor) in hosts that have other
// unmanaged sibling nodes on body — which is any host without Vuetify's
// `.v-application` root, not just this widget's own test pages. Resolve to a
// dedicated, persistent element instead of ever letting it fall through to
// `document.body`.
const dialogAnchorSelector = ref(resolveDialogAnchor());

function resolveDialogAnchor(): string {
    if (document.querySelector('.v-application')) return '.v-application';

    const id = 'nw-chat-widget-teleport-root';
    if (!document.getElementById(id)) {
        const root = document.createElement('div');
        root.id = id;
        Object.assign(root.style, {
            position: 'fixed',
            inset: '0',
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex: '1010',
        });
        document.body.appendChild(root);
    }
    return `#${id}`;
}

// Always start false so nw-dialog's fade-in watcher fires on the first open.
// nw-dialog initialises its internal state from modelValue but only runs the
// animation when it observes a false→true transition; starting true skips it.
const dialogOpen = ref(false);

onMounted(() => {
    if (props.open)
        nextTick(() => {
            dialogOpen.value = true;
        });
});

watch(
    () => props.open,
    (val: boolean) => {
        if (!val) resetConversation();
        dialogOpen.value = val;
    },
);
</script>
